import { QueryClient, onlineManager } from '@tanstack/react-query'
import { persistQueryClient } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'

/**
 * React Query client configuration
 * Provides caching, background refetching, and optimistic updates
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is fresh for 5 minutes
      staleTime: 1000 * 60 * 5,
      // Keep unused data in cache for 30 minutes
      gcTime: 1000 * 60 * 30,
      // Don't refetch on window focus by default (can be overridden per query)
      refetchOnWindowFocus: false,
      // Retry failed requests once
      retry: 1,
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
})

// Set up offline persistence using localStorage
const localStoragePersister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: 'petru-query-cache',
  throttleTime: 1000,
})

// Persist query cache to localStorage for offline support
if (typeof window !== 'undefined') {
  persistQueryClient({
    queryClient,
    persister: localStoragePersister,
    maxAge: 1000 * 60 * 60 * 24, // 24 hours
    buster: '1', // Increment to invalidate cached data on breaking changes
  })

  // Listen for online/offline events
  window.addEventListener('online', () => {
    onlineManager.setOnline(true)
  })

  window.addEventListener('offline', () => {
    onlineManager.setOnline(false)
  })
}

export default queryClient
